import { useClient } from '@cortex/react-query';
import type { CreateJudgeInput, Judge } from '@cortex/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { PageHeader } from '../../components/common/PageHeader';
import {
  JudgeEditor,
  defaultJudgeEditorState,
  type IJudgeEditorState,
} from '../../components/judges/JudgeEditor';
import { routes } from '../../lib/constants';

export const Route = createFileRoute('/judges/new')({ component: NewJudgePage });

function NewJudgePage() {
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [state, setState] = useState<IJudgeEditorState>(defaultJudgeEditorState);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const payload: CreateJudgeInput = {
        judgeType: state.judgeType,
        name: state.name,
        description: state.description.trim() || undefined,
        inputSchema: state.inputSchema as never,
        outputSchema: state.outputSchema,
        ...(state.judgeType === 'CODE'
          ? { code: state.code }
          : {
              modelConfigId: state.modelConfigId,
              systemPrompt: state.systemPrompt,
              ...(state.promptTemplate.trim() ? { promptTemplate: state.promptTemplate } : {}),
            }),
      } as CreateJudgeInput;
      return client.http.post('v1/judges', { json: payload }).json<Judge>();
    },
    onSuccess: async (j) => {
      setSubmitErr(null);
      try {
        await qc.invalidateQueries({ queryKey: ['judges'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.judgeDetail(j.id) });
      } catch (err) {
        console.error(err);
      }
    },
    onError: (e) => setSubmitErr((e as Error).message),
  });

  return (
    <>
      <PageHeader title="New judge" actions={<BackButton to={routes.JUDGES} label="Judges" />} />
      <JudgeEditor state={state} setState={setState} typeLocked={false} />
      <div style={{ marginTop: 'var(--sp-4)', display: 'flex', gap: 'var(--sp-2)' }}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={create.isPending || !state.name.trim()}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'Creating…' : 'Create judge'}
        </button>
      </div>
      {submitErr ? <div className="alert alert--danger">{submitErr}</div> : null}
    </>
  );
}
